import { Static, Type } from '@sinclair/typebox';
import { NotificationChannelSchema } from './notification';

export const adminDashboardApiPrefix = 'app/admin' as const;

export enum CronRunStatus {
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
  RUNNING = 'RUNNING',
}

const HeartbeatSeveritySchema = Type.Union([
  Type.Literal('ok'),
  Type.Literal('warn'),
  Type.Literal('alert'),
  Type.Literal('idle'),
]);

const CircuitStateSchema = Type.Union([Type.Literal('closed'), Type.Literal('half-open'), Type.Literal('open')]);

const CronStatusSchema = Type.Union([
  ...Object.values(CronRunStatus).map((v: string): ReturnType<typeof Type.Literal> => Type.Literal(v)),
  Type.Null(),
]);

export const AdminHeartbeatItemSchema = Type.Object({
  key: Type.String(),
  label: Type.String(),
  last_at_relative: Type.Union([Type.String(), Type.Null()]),
  threshold_hours: Type.Number(),
  severity: HeartbeatSeveritySchema,
  context: Type.Object({
    professional_name: Type.Union([Type.String(), Type.Null()]),
    customer_name: Type.Union([Type.String(), Type.Null()]),
    amount_formatted: Type.Union([Type.String(), Type.Null()]),
    extra: Type.Union([Type.String(), Type.Null()]),
  }),
});
export type AdminHeartbeatItemObject = Static<typeof AdminHeartbeatItemSchema>;

export const AdminHeartbeatResponseSchema = Type.Object({
  items: Type.Array(AdminHeartbeatItemSchema),
});
export type AdminHeartbeatResponseObject = Static<typeof AdminHeartbeatResponseSchema>;

/**
 * Qui exploite le service appelé — la question à laquelle le monitoring doit répondre en un
 * coup d'œil : « la panne est-elle chez nous ou chez un tiers ? ».
 * INTERNAL = brique qu'on héberge et qu'on peut réparer soi-même (k8s / VPS / v1).
 * EXTERNAL = SaaS tiers : on ne peut que constater, contourner ou ouvrir un ticket.
 */
export enum ProviderOwnership {
  INTERNAL = 'internal',
  EXTERNAL = 'external',
}

export const AdminProviderItemSchema = Type.Object({
  provider: Type.String(),
  display_name: Type.String(),
  ownership: Type.Enum(ProviderOwnership),
  circuit_state: CircuitStateSchema,
  total_calls: Type.Number(),
  success_count: Type.Number(),
  error_count: Type.Number(),
  success_rate: Type.Number(),
  avg_duration_ms: Type.Number(),
  last_error_at_relative: Type.Union([Type.String(), Type.Null()]),
  last_error_message: Type.Union([Type.String(), Type.Null()]),
});
export type AdminProviderItemObject = Static<typeof AdminProviderItemSchema>;

export const AdminProvidersResponseSchema = Type.Object({
  items: Type.Array(AdminProviderItemSchema),
  window_hours: Type.Number(),
});
export type AdminProvidersResponseObject = Static<typeof AdminProvidersResponseSchema>;

export const AdminQueueItemSchema = Type.Object({
  name: Type.String(),
  waiting: Type.Number(),
  active: Type.Number(),
  completed: Type.Number(),
  failed: Type.Number(),
  delayed: Type.Number(),
});
export type AdminQueueItemObject = Static<typeof AdminQueueItemSchema>;

export const AdminQueuesResponseSchema = Type.Object({
  items: Type.Array(AdminQueueItemSchema),
});
export type AdminQueuesResponseObject = Static<typeof AdminQueuesResponseSchema>;

export const AdminFailedJobItemSchema = Type.Object({
  id: Type.String(),
  name: Type.String(),
  event_type: Type.Union([Type.String(), Type.Null()]),
  reason: Type.String(),
  attempts: Type.Number(),
  failed_at_relative: Type.Union([Type.String(), Type.Null()]),
});
export type AdminFailedJobItemObject = Static<typeof AdminFailedJobItemSchema>;

export const AdminFailedJobsResponseSchema = Type.Object({
  queue: Type.String(),
  total: Type.Number(),
  items: Type.Array(AdminFailedJobItemSchema),
});
export type AdminFailedJobsResponseObject = Static<typeof AdminFailedJobsResponseSchema>;

export const AdminCronItemSchema = Type.Object({
  name: Type.String(),
  schedule: Type.String(),
  last_run_relative: Type.Union([Type.String(), Type.Null()]),
  last_status: CronStatusSchema,
  last_duration_ms: Type.Union([Type.Number(), Type.Null()]),
  last_error: Type.Union([Type.String(), Type.Null()]),
});
export type AdminCronItemObject = Static<typeof AdminCronItemSchema>;

export const AdminCronsResponseSchema = Type.Object({
  items: Type.Array(AdminCronItemSchema),
});
export type AdminCronsResponseObject = Static<typeof AdminCronsResponseSchema>;

export const AdminNotificationChannelTotalSchema = Type.Object({
  channel: NotificationChannelSchema,
  total: Type.Number(),
  sent: Type.Number(),
  failed: Type.Number(),
  skipped: Type.Number(),
  fail_rate: Type.Number(),
});
export type AdminNotificationChannelTotalObject = Static<typeof AdminNotificationChannelTotalSchema>;

export const AdminNotificationTopErrorSchema = Type.Object({
  message: Type.String(),
  count: Type.Number(),
});
export type AdminNotificationTopErrorObject = Static<typeof AdminNotificationTopErrorSchema>;

export const AdminNotificationTopProSchema = Type.Object({
  professional_id: Type.Number(),
  professional_name: Type.String(),
  count: Type.Number(),
});
export type AdminNotificationTopProObject = Static<typeof AdminNotificationTopProSchema>;

export const AdminNotificationsResponseSchema = Type.Object({
  window_hours: Type.Number(),
  totals_by_channel: Type.Array(AdminNotificationChannelTotalSchema),
  top_errors: Type.Array(AdminNotificationTopErrorSchema),
  top_pros: Type.Array(AdminNotificationTopProSchema),
});
export type AdminNotificationsResponseObject = Static<typeof AdminNotificationsResponseSchema>;

export const AdminErrorItemSchema = Type.Object({
  source: Type.Union([Type.Literal('external_api'), Type.Literal('notification')]),
  provider: Type.Union([Type.String(), Type.Null()]),
  channel: Type.Union([NotificationChannelSchema, Type.Null()]),
  status_code: Type.Union([Type.Number(), Type.Null()]),
  message: Type.String(),
  professional_id: Type.Union([Type.Number(), Type.Null()]),
  occurred_at_formatted: Type.String(),
  occurred_at_relative: Type.String(),
});
export type AdminErrorItemObject = Static<typeof AdminErrorItemSchema>;

export const AdminErrorsResponseSchema = Type.Object({
  items: Type.Array(AdminErrorItemSchema),
  window_hours: Type.Number(),
});
export type AdminErrorsResponseObject = Static<typeof AdminErrorsResponseSchema>;

// =====================================================================
// Database stats — GET /admin/monitoring/database
// =====================================================================

export const AdminDbTableStatSchema = Type.Object({
  table_name: Type.String(),
  total_bytes: Type.Number(),
  total_size: Type.String(),
  table_size: Type.String(),
  indexes_size: Type.String(),
  live_rows: Type.Number(),
  dead_rows: Type.Number(),
  dead_ratio: Type.Number(),
  share_of_db: Type.Number(),
  last_autovacuum_relative: Type.Union([Type.String(), Type.Null()]),
});
export type AdminDbTableStatObject = Static<typeof AdminDbTableStatSchema>;

export const AdminDatabaseResponseSchema = Type.Object({
  db_size: Type.String(),
  db_size_bytes: Type.Number(),
  total_live_rows: Type.Number(),
  total_dead_rows: Type.Number(),
  tables: Type.Array(AdminDbTableStatSchema),
});
export type AdminDatabaseResponseObject = Static<typeof AdminDatabaseResponseSchema>;
